import type { FilterField, GroupedSelectFilterField } from '@ooh/ui/components/filter-panel';
import { bucketGroupedFilterValues, namespacedKey } from '@ooh/ui/lib/grouped-filter';
import type { OfferUnitFilterOptionsResponse } from '@ooh/contracts';
import { MEDIA_FORMAT_LABELS } from '@ooh/contracts';
import { buildRegionOptions as buildRegionOptionsShared } from '../../../../lib/region-options';
import { buildMediaTypeOptions } from '../../../../lib/media-type-options';
import { RANGE_FIELD_DEFS } from '../../../../lib/range-field-defs';

/**
 * Offer-units filter-panel field set. Mirrors the catalog `buildFilterFields`
 * (features/media-units/lib/filter-fields.ts) for full filter-set parity: it
 * emits mediaType, region/county/agglomeration/city merged into the combined
 * "Lokalizacja" pill, format, supplier, lighting, grwId, material,
 * multiformat, tags, address, and the numeric range dimensions
 * (area/width/height/audience + every COST_RANGE_DIMENSIONS entry,
 * CU-86ca9k73a), sourcing options from the offer-scoped filter-options query
 * rather than the whole catalog. Only mediaType + the combined location pill
 * are pinned — the same dimensions the catalog pins (`media-unit-filter-ui`
 * spec), reusing the pre-existing combined Lokalizacja pill instead of the
 * catalog's separate county/city fields.
 *
 * Cross-feature import of `features/media-units` is forbidden
 * (WEB_ARCHITECTURE.md:36). The region-normalization, media-type-options, and
 * range-field-def helpers are shared via the neutral `src/lib/` instead of
 * being duplicated here.
 */

/** Panel field ids in their server-`filters` order (`location` fans out to 4 real keys). */
export const OFFER_UNIT_FILTER_FIELD_IDS = [
  'mediaType',
  'location',
  'format',
  'supplier',
  'lighting',
  'supplierNumber',
  'grwId',
  // ─── Catalog-parity filters (CU-86ca9k73a) ───
  'material',
  'multiformat',
  'tags',
] as const;

export type OfferUnitFilterFieldId = (typeof OFFER_UNIT_FILTER_FIELD_IDS)[number];

/** Translation-free labels for each offer-units filter field (passed in by the page). */
export interface OfferUnitFilterFieldLabels {
  region: string;
  county: string;
  agglomeration: string;
  city: string;
  format: string;
  mediaType: string;
  /** Localized labels for each MediaTypeEnum value, keyed by enum value. */
  mediaTypeOptionLabels: Record<string, string>;
  supplier: string;
  lighting: string;
  supplierNumber: string;
  grwId: string;
  /** Combined "Lokalizacja" field labels — consumed by `buildLocationFilterField`. */
  location: string;
  locationSearchPlaceholder: string;
  locationInfoHint: string;
  locationInfoHintLabel: string;
  /** Placeholder shared by every `text` field (currently only `address`). */
  searchPlaceholder: string;
  // ─── Catalog-parity filter labels (CU-86ca9k73a) ───
  material: string;
  multiformat: string;
  tags: string;
  address: string;
  area: string;
  width: string;
  height: string;
  audience: string;
  catalogPrice: string;
  rent: string;
  exposureCost: string;
  additionalCosts: string;
  discountedPrice: string;
  lastMinutePrice: string;
  starterPrice: string;
  productionCost: string;
  installationCost: string;
  dismantlingCost: string;
  clientProduction: string;
  clientInstallation: string;
  clientDismantling: string;
  clientAdditionalFees: string;
  // ─── Column-parity filter labels (catalog-column-filter-parity) ───
  notes: string;
  roadNumber: string;
}

const EMPTY_OFFER_UNIT_FILTER_OPTIONS: OfferUnitFilterOptionsResponse = {
  regions: [],
  counties: [],
  agglomerations: [],
  cities: [],
  formats: [],
  suppliers: [],
  supplierNumbers: [],
  grwIds: [],
  lightingValues: [],
  mediaTypes: [],
  materials: [],
  multiformats: [],
  tagOptions: [],
};

/** Identity option mapping for plain string dimensions (value === label). */
function toStringOptions(values: string[]): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: v }));
}

/**
 * Maps raw API region strings to canonical display options. Unrecognized
 * values keep their raw string as the display label (no capitalization),
 * matching this feature's pre-existing behavior. See
 * `src/lib/region-options.ts` for the full shared normalization behavior
 * (also used by the catalog's `features/media-units/lib/filter-fields.ts`).
 */
export function buildRegionOptions(regions: string[]): Array<{ value: string; label: string }> {
  return buildRegionOptionsShared(regions);
}

/** The four location dimension group definitions merged into the combined "Lokalizacja" pill. */
const LOCATION_GROUPS: ReadonlyArray<{
  id: string;
  headingLabelKey: 'region' | 'county' | 'agglomeration' | 'city';
  buildOptions: (opts: OfferUnitFilterOptionsResponse) => Array<{ value: string; label: string }>;
}> = [
  { id: 'region', headingLabelKey: 'region', buildOptions: (o) => buildRegionOptions(o.regions) },
  { id: 'county', headingLabelKey: 'county', buildOptions: (o) => toStringOptions(o.counties) },
  {
    id: 'agglomeration',
    headingLabelKey: 'agglomeration',
    buildOptions: (o) => toStringOptions(o.agglomerations),
  },
  { id: 'city', headingLabelKey: 'city', buildOptions: (o) => toStringOptions(o.cities) },
];

export const LOCATION_GROUP_FIELD_IDS = LOCATION_GROUPS.map((g) => g.id);

/**
 * Builds the combined "Lokalizacja" grouped field — region + county +
 * agglomeration + city merged into ONE `groupedSelect` field with OR
 * semantics, catalog parity (see `features/media-units/lib/filter-fields.ts`).
 */
export function buildLocationFilterField(
  opts: OfferUnitFilterOptionsResponse | undefined,
  labels: OfferUnitFilterFieldLabels,
): GroupedSelectFilterField | null {
  if (!opts) return null;
  return {
    type: 'groupedSelect',
    id: 'location',
    label: labels.location,
    pinned: true,
    searchPlaceholder: labels.locationSearchPlaceholder,
    infoHint: labels.locationInfoHint,
    infoHintLabel: labels.locationInfoHintLabel,
    groups: LOCATION_GROUPS.map((g) => ({
      id: g.id,
      heading: labels[g.headingLabelKey],
      options: g.buildOptions(opts),
    })),
  };
}

/**
 * Builds the numeric `range` fields, gated on the shared `RANGE_FIELD_DEFS`
 * `costGated` flag — the catalogue's own split gate: client price dimensions
 * stay addable to any caller, and every internal cost dimension only to a
 * caller who may see costs (offer-unified-table, CU-86cbc3r8b). The retired
 * stricter all-14-gated rule existed only because the offer unit listing
 * carried no cost field at all; task 1.1 widened it, removing that reason to
 * deviate from the catalogue. Additionally omits the rent range field when
 * canViewRent is false, on top of (not instead of) canViewCosts — rent's own
 * wildcard cost.view_rent gate is narrower (field-visibility, CU-86cbgnt3z).
 */
function buildOfferUnitRangeFields(
  labels: OfferUnitFilterFieldLabels,
  canViewCosts: boolean,
  canViewRent: boolean,
): FilterField[] {
  return RANGE_FIELD_DEFS.filter(
    (def) => (canViewCosts || !def.costGated) && (canViewRent || !def.rentGated),
  ).map((def) => ({
    type: 'range',
    id: def.id,
    label: labels[def.id],
    unit: def.unit,
  }));
}

/**
 * Builds the column-parity filter fields (notes/roadNumber —
 * catalog-column-filter-parity). Mirrors the catalog's equivalent
 * (features/media-units/lib/filter-fields.ts).
 *
 * `status` was removed entirely (CU-86cb1nq7h follow-up) — redundant with
 * detailed availability. `supplierEmail`/`supplierPhone`/`supplierContactInfo`
 * were removed entirely (CU-86cb1nq7h, client request) — see
 * `OFFER_UNIT_FILTER_FIELD_IDS` doc comment.
 */
function buildOfferUnitColumnParityFields(labels: OfferUnitFilterFieldLabels): FilterField[] {
  return [
    { type: 'text', id: 'notes', label: labels.notes, placeholder: labels.searchPlaceholder },
    {
      type: 'text',
      id: 'roadNumber',
      label: labels.roadNumber,
      placeholder: labels.searchPlaceholder,
    },
  ];
}

/**
 * Builds the catalog-parity fields (material/multiformat/tags/address + the
 * numeric ranges, CU-86ca9k73a, + the column-parity fields) — extracted so
 * `buildOfferUnitFilterFields` stays within the file's
 * complexity/max-lines-per-function budget. See `buildFilterFields`'s
 * `buildCatalogOnlyFields` (media-units) for the catalog equivalent.
 */
function buildOfferUnitParityFields(
  opts: OfferUnitFilterOptionsResponse,
  labels: OfferUnitFilterFieldLabels,
  canViewCosts: boolean,
  canViewRent: boolean,
): FilterField[] {
  return [
    {
      type: 'select',
      id: 'material',
      label: labels.material,
      options: (opts.materials ?? []).map((m) => ({ value: m, label: m })),
    },
    {
      type: 'select',
      id: 'multiformat',
      label: labels.multiformat,
      options: (opts.multiformats ?? []).map((m) => ({ value: m, label: m })),
    },
    {
      type: 'select',
      id: 'tags',
      label: labels.tags,
      options: (opts.tagOptions ?? []).map((tag) => ({ value: tag.code, label: tag.label })),
    },
    {
      type: 'text',
      id: 'address',
      label: labels.address,
      placeholder: labels.searchPlaceholder,
    },
    ...buildOfferUnitColumnParityFields(labels),
    ...buildOfferUnitRangeFields(labels, canViewCosts, canViewRent),
  ];
}

/**
 * Builds the units-tab filter-panel fields. Only mediaType and the combined
 * "Lokalizacja" field are pinned (the starter set) — catalog parity, exactly
 * matching the pinned set in `media-unit-filter-ui` spec. format/supplier/
 * lighting/supplierNumber/grwId/material/multiformat/tags/address/ranges are
 * unpinned so the panel surfaces them under "Dodaj filtr". Format options
 * carry the format CODE as value (display label from MEDIA_FORMAT_LABELS),
 * supplier options carry the supplier ID as value — so panel selections are
 * codes/ids, not labels.
 *
 * `canViewCosts` (default true) gates the cost-range dimensions exactly as
 * the catalog does (`RANGE_FIELD_DEFS.costGated`): every client price
 * dimension stays addable to any caller, and every internal cost dimension
 * only to a caller who may see costs (offer-unified-table, CU-86cbc3r8b).
 *
 * Deliberate deviation from the catalog: supplierNumber/grwId stay `type:
 * 'select'` here (not `type: 'text'`) — the catalog's free-text search box
 * already covers supplierNumber/grwId substring matches, and this feature has
 * no equivalent standalone free-text search input outside FilterPanel. Keeping
 * them as working `select` dropdowns here preserves real functionality
 * instead of importing a dead field from the catalog.
 */
export function buildOfferUnitFilterFields(
  options: OfferUnitFilterOptionsResponse | undefined,
  labels: OfferUnitFilterFieldLabels,
  canViewCosts = true,
  canViewSupplierDetails = true,
  canViewRent = true,
): FilterField[] {
  const opts = options ?? EMPTY_OFFER_UNIT_FILTER_OPTIONS;
  const formatOptions = opts.formats.map((f) => ({
    value: f.code,
    label: MEDIA_FORMAT_LABELS[f.code] ?? f.label,
  }));
  const supplierOptions = opts.suppliers.map((s) => ({ value: String(s.id), label: s.name }));
  const locationField = buildLocationFilterField(opts, labels);
  // `supplier`/`supplierNumber` are omitted (not disabled) when
  // canViewSupplierDetails is false — closes the same UI-level supplier
  // filter side channel the catalog's buildSupplierIdentityFields closes
  // (catalog-column-filter-parity).
  const supplierField: FilterField[] = canViewSupplierDetails
    ? [{ type: 'select', id: 'supplier', label: labels.supplier, options: supplierOptions }]
    : [];
  const supplierNumberField: FilterField[] = canViewSupplierDetails
    ? [
        {
          type: 'select',
          id: 'supplierNumber',
          label: labels.supplierNumber,
          options: toStringOptions(opts.supplierNumbers),
        },
      ]
    : [];
  return [
    {
      type: 'select',
      id: 'mediaType',
      label: labels.mediaType,
      pinned: true,
      options: buildMediaTypeOptions(opts.mediaTypes, labels.mediaTypeOptionLabels),
    },
    ...(locationField ? [locationField] : []),
    { type: 'select', id: 'format', label: labels.format, options: formatOptions },
    ...supplierField,
    {
      type: 'select',
      id: 'lighting',
      label: labels.lighting,
      options: toStringOptions(opts.lightingValues),
    },
    ...supplierNumberField,
    { type: 'select', id: 'grwId', label: labels.grwId, options: toStringOptions(opts.grwIds) },
    ...buildOfferUnitParityFields(opts, labels, canViewCosts, canViewRent),
  ];
}

// ─── Combined "Lokalizacja" pill value/onChange adapter ───────────────────────
// Copied from `features/media-units/lib/filter-fields.ts` (cross-feature
// import forbidden — see file header).

/**
 * Merges the four location dimension values into ONE flat array for the
 * combined pill's value, namespacing each entry as `${id}::${value}`.
 */
export function buildLocationPanelValue(filterValue: Record<string, string[]>): string[] {
  return LOCATION_GROUP_FIELD_IDS.flatMap((id) =>
    (filterValue[id] ?? []).map((value) => namespacedKey(id, value)),
  );
}

/**
 * Wraps a FilterPanel `onChange` handler so a flat value update on the
 * combined "location" field is bucketed back into the underlying
 * region/county/agglomeration/city keys. Non-location updates pass through
 * untouched.
 */
export function adaptLocationFilterChange(
  locationField: GroupedSelectFilterField | null,
  onChange: (value: Record<string, string[]>) => void,
): (value: Record<string, string[]>) => void {
  return (value) => {
    if (!locationField || !('location' in value)) {
      onChange(value);
      return;
    }
    const { location, ...rest } = value;
    onChange({ ...rest, ...bucketGroupedFilterValues(locationField.groups, location ?? []) });
  };
}

// ─── Combined FilterPanel change split (CU-86ca9k73a) ─────────────────────────

/** Field ids handled by the numeric-range sidecar (RANGE_FIELD_DEFS ids). */
const RANGE_FIELD_ID_SET = new Set<string>(RANGE_FIELD_DEFS.map((def) => def.id));

export interface SplitFilterPanelChangeResult {
  /** Range dimension entries present in the change, keyed by dimension id. */
  rangeSubset: Record<string, string[]>;
  /** Trimmed next address value, or undefined when the `address` key was absent from the change. */
  address: string | undefined;
  /** Every other field id (mediaType/format/supplier/lighting/.../location/material/multiformat/tags). */
  rest: Record<string, string[]>;
}

/**
 * Splits FilterPanel's single combined `onChange` value object into its
 * three destinations: numeric-range dimensions (scalar `<id>_min`/`<id>_max`
 * sidecar), the dedicated `address` text field (scalar sidecar), and
 * everything else (forwarded to `onFilterChange`, including "location" —
 * still bucketed separately by `adaptLocationFilterChange`).
 *
 * Extracted as a pure function so this partitioning logic is unit-testable
 * without rendering `UnitsTabPage` (see `lib/__tests__/filter-fields.parity.test.ts`).
 */
export function splitFilterPanelChange(
  nextValue: Record<string, string[]>,
): SplitFilterPanelChangeResult {
  const rangeSubset: Record<string, string[]> = {};
  const rest: Record<string, string[]> = {};
  let address: string | undefined;
  for (const [key, values] of Object.entries(nextValue)) {
    if (RANGE_FIELD_ID_SET.has(key)) rangeSubset[key] = values;
    else if (key === 'address') address = values[0]?.trim() ?? '';
    else rest[key] = values;
  }
  return { rangeSubset, address, rest };
}
